import { CsvRowProcessResult, UserCsvRowProcessResult } from '../types';

export const escapeCsvField = (field: any): string => {
  if (field === null || field === undefined) return '';
  const stringField = String(field);
  if (stringField.includes('"') || stringField.includes(',') || stringField.includes('\n') || stringField.includes('\r')) {
    return `"${stringField.replace(/"/g, '""')}"`;
  }
  return stringField;
};

// Consolidated parseCSV function
export const parseCSV = (csvText: string): { headers: string[], rows: string[][] } => {
  let textToParse = csvText;
  // Remove BOM if present
  if (textToParse.charCodeAt(0) === 0xFEFF) {
    textToParse = textToParse.substring(1);
  }
  const lines = textToParse.trim().split(/\r\n|\n|\r/);
  
  const parseLine = (line: string): string[] => {
      const result: string[] = [];
      let currentField = "";
      let inQuotes = false;
      for (let i = 0; i < line.length; i++) {
          const char = line[i];
          if (char === '"') {
              if (inQuotes && i + 1 < line.length && line[i+1] === '"') {
                  currentField += '"';
                  i++;
              } else {
                  inQuotes = !inQuotes;
              }
          } else if (char === ',' && !inQuotes) {
              result.push(currentField.trim());
              currentField = "";
          } else {
              currentField += char;
          }
      }
      result.push(currentField.trim());
      return result;
  };
  
  const headers = lines[0] ? parseLine(lines[0]).map(h => h.toLowerCase().replace(/\s+/g, '')) : []; 
  const rows = lines.slice(1).filter(line => line.trim() !== '').map(line => parseLine(line));
  return { headers, rows };
};


export const downloadResultsCSV = (results: CsvRowProcessResult[], originalFileName: string) => {
  const csvHeaders = ["Fila Original (CSV de entrada)", "Código Inmueble (CSV de entrada)", "Tasa Comisión (CSV de entrada)", "Estrategia Comercial (CSV de entrada)", "Estado del Procesamiento", "Detalle del Resultado", "ID Firestore (si aplica)"];
  
  const csvRows = results.map(res => [
      escapeCsvField(res.originalRowNumber),
      escapeCsvField(res.originalPropertyCode),
      escapeCsvField(res.originalCommissionRate),
      escapeCsvField(res.estrategiaComercial),
      escapeCsvField(res.status),
      escapeCsvField(res.details),
      escapeCsvField(res.firestoreId)
  ].join(','));

  const csvContent = [
    "\uFEFF", // BOM for UTF-8
    csvHeaders.join(','),
    ...csvRows
  ].join("\n");
  
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement("a");
  const url = URL.createObjectURL(blob);
  link.setAttribute("href", url);
  link.setAttribute("download", `Resultados_Carga_${originalFileName.replace(/\.[^/.]+$/, "")}.csv`);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

export const downloadUserResultsCSV = (results: UserCsvRowProcessResult[], originalFileName: string) => {
  const csvHeaders = ["Fila Original", "Email", "Estado", "Detalles", "ID Firestore"];
  
  const csvRows = results.map(res => [
      escapeCsvField(res.originalRowNumber),
      escapeCsvField(res.email),
      escapeCsvField(res.status),
      escapeCsvField(res.details),
      escapeCsvField(res.firestoreId)
  ].join(','));

  const csvContent = [
    "\uFEFF",
    csvHeaders.join(','),
    ...csvRows
  ].join("\n");
  
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement("a");
  const url = URL.createObjectURL(blob);
  link.setAttribute("href", url);
  link.setAttribute("download", `Resultados_Usuarios_${originalFileName.replace(/\.[^/.]+$/, "")}.csv`);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

export const downloadUserTemplate = (): { content: string, fileName: string } => {
  const headers = ["email", "displayName", "username", "role", "status"];
  const exampleRows = [
    ["nuevo.broker@email.com", "Nuevo Broker Ejemplo", "nuevo.broker", "BROKER", "Pendiente"],
    ["usuario.existente@email.com", "Usuario Existente", "usuario.existente", "COMERCIAL_INTERNO", "Activo"],
  ];
  
  let csvContent = "\uFEFF";
  csvContent += headers.join(",") + "\n";
  exampleRows.forEach(row => {
    csvContent += row.map(field => `"${String(field || '').replace(/"/g, '""')}"`).join(",") + "\n";
  });

  return {
    content: csvContent,
    fileName: "Plantilla_Carga_Usuarios.csv",
  };
};


// Centralized downloadPropertyTemplate function
export const downloadPropertyTemplate = (type: 'complete' | 'quick' | 'allied'): { content: string, fileName: string } => {
  let csvContent = "\uFEFF"; // BOM for UTF-8
  let fileName = "";
  let headers: string[] = [];
  let exampleRows: string[][] = [];

  if (type === 'complete') {
    headers = [
      "codigo", "tipoinmueble", "ciudad", "direccion", "descripcion", 
      "precio_venta", "valor_administracion", "commissionRate", "estrato",
      "area_construida_original", "area_privada", "num_habitaciones", "num_banos", "num_parqueaderos",
      "status", "owner", "features", "realEstateRegistration", "transactionType", "neighborhoodName",
      "latitude", "longitude", "constructionYear", "remodelationYear", "propertyCondition",
      "vigilanceType", "antiguedad", "estrategiaComercial", "currencyCode", "video_youtube_url"
    ];
    exampleRows.push([
      "CC-12345", "Apartamento", "Bogotá", "Calle Falsa 123", "Lindo apartamento con excelentes acabados y vista panorámica.", 
      "250000000", "200000", "0.03", "4", "70 m²", "65 m²", "3", "2", "1",
      "Activo", "Cien Cuadras", "Balcón;Estudio;Gimnasio", "50C-7890123", "Venta", "Chapinero",
      "4.60971", "-74.08175", "2010", "2022", "Remodelado", 
      "Privada 24hrs", "14", "Oportunidad única, precio negociable", "COP", "https://www.youtube.com/watch?v=example"
    ]);
    fileName = "Plantilla_Carga_Completa_Inmuebles.csv";
  } else if (type === 'quick') { 
    headers = ["codigo", "commissionRate", "estrategiaComercial"];
    exampleRows.push(["183150-API123", "1.5%", "Estrategia de lanzamiento Mayo"]);
    exampleRows.push(["183150-XYZ789", "0.02", "Descuento especial por aniversario"]);
    fileName = "Plantilla_Actualizacion_Rapida_Inmuebles.csv";
  } else { // 'allied' template
    headers = ["codigo", "commissionRate", "estrategiaComercial"];
    exampleRows.push(["CODALIADO-001", "2.5%", "Estrategia Aliado Junio"]);
    exampleRows.push(["CODALIADO-002", "0.03", "Oferta Especial Aliado"]);
    fileName = "Plantilla_Carga_Inmuebles_Aliados.csv";
  }
  
  csvContent += headers.join(",") + "\n";
  exampleRows.forEach(row => {
    csvContent += row.map(field => `"${String(field || '').replace(/"/g, '""')}"`).join(",") + "\n";
  });

  return { content: csvContent, fileName };
};

export const downloadInventoryCSV = (properties: any[], fileNamePrefix: string = "Inventario_Detallado") => {
  const headers = [
    "Código", "Código BRP", "Tipo Inmueble", "Ciudad", "Localidad", "Barrio", "Dirección",
    "Precio Venta", "Administración", "Comisión (%)", "Estrato", "Área Construida", "Área Privada",
    "Habitaciones", "Baños", "Parqueaderos", "Estado", "Propietario", "Estrategia Comercial",
    "Visible Broker", "Roles Visibles", "Creado Manualmente", "Fecha Agregado", "Descripción"
  ];

  const rows = properties.map(p => [
    escapeCsvField(p.codigo),
    escapeCsvField(p.codigo_brp),
    escapeCsvField(p.tipoinmueble),
    escapeCsvField(p.ciudad),
    escapeCsvField(p.localidad),
    escapeCsvField(p.neighborhoodName),
    escapeCsvField(p.direccion),
    escapeCsvField(p.precio_venta),
    escapeCsvField(p.valor_administracion),
    escapeCsvField(p.commissionRate),
    escapeCsvField(p.estrato),
    escapeCsvField(p.area_construida_original),
    escapeCsvField(p.area_privada),
    escapeCsvField(p.num_habitaciones),
    escapeCsvField(p.num_banos),
    escapeCsvField(p.num_parqueaderos),
    escapeCsvField(p.status),
    escapeCsvField(p.owner),
    escapeCsvField(p.estrategiaComercial),
    escapeCsvField(p.es_visible_broker ? 'SÍ' : 'NO'),
    escapeCsvField(p.visible_roles?.join(', ') || 'TODOS'),
    escapeCsvField(p.creado_manualmente ? 'SÍ' : 'NO'),
    escapeCsvField(p.fechaAgregadoNuevo?.toDate?.().toLocaleDateString() || ''),
    escapeCsvField(p.descripcion)
  ].join(','));

  const csvContent = [
    "\uFEFF", // BOM for UTF-8
    headers.join(','),
    ...rows
  ].join("\n");

  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement("a");
  const url = URL.createObjectURL(blob);
  const timestamp = new Date().toISOString().split('T')[0];
  link.setAttribute("href", url);
  link.setAttribute("download", `${fileNamePrefix}_${timestamp}.csv`);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};
